import { useLocation, useNavigate } from "@tanstack/react-router"
import clsx from "clsx"
import { CheckCircle2, Circle } from "lucide-react"

import { membershipApplicationSteps } from "../constants"
import type {
  MembershipApplicationStep,
  MembershipApplicationStepId,
} from "../types"
import type { MembershipCategorySectionConfig } from "@/utils/membership/types"

interface ApplicationProgressSidebarProps {
  steps?: MembershipApplicationStep[]
  completedStepIds?: MembershipApplicationStepId[]
  sections?: Partial<Record<MembershipApplicationStepId, MembershipCategorySectionConfig>>
  categoryName?: string
}

export function ApplicationProgressSidebar({
  steps = membershipApplicationSteps,
  completedStepIds,
  sections,
  categoryName,
}: ApplicationProgressSidebarProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return (
    <aside className="flex flex-col gap-6 rounded-lg border border-border bg-card p-6">
      <header className="space-y-2">
        {/* <div className="flex items-center gap-2"> */}
          <h2 className="text-lg font-semibold">Membership Application</h2>
          {categoryName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              <span className="h-1 w-1 rounded-full bg-primary" />
              {categoryName}
            </span>
          )}
        {/* </div> */}
        <p className="text-sm text-muted-foreground">
          Work through each section and keep an eye on your progress as you go.
        </p>
      </header>
      <nav className="flex flex-col gap-2">
        {steps.map((step) => {
          const isCompleted = completedStepIds?.includes(step.id) ?? false
          const isActive =
            pathname === step.path || pathname.startsWith(`${step.path}/`)
          const sectionConfig = sections?.[step.id]
          const isOptional = sectionConfig?.isRequired === false

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => navigate({ to: step.path })}
              className={clsx(
                "group flex w-full items-start gap-3 rounded-lg border border-transparent px-3 py-2 text-left transition",
                isActive && "border-primary bg-primary/5",
                !isActive && "hover:border-border hover:bg-muted/60",
              )}
            >
              <StepIcon isActive={isActive} isCompleted={isCompleted} />
              <span className="flex flex-1 flex-col gap-1">
                <span className="text-sm font-medium text-foreground">
                  {step.title}
                  {isOptional ? (
                    <span className="ml-2 text-xs font-semibold uppercase text-muted-foreground">
                      Optional
                    </span>
                  ) : <span className="ml-2 text-xs font-light uppercase text-destructive">*</span>}
                </span>
                {step.description ? (
                  <span className="text-xs text-muted-foreground">
                    {step.description}
                  </span>
                ) : null}
              </span>
            </button>
          )
        })}
      </nav>
    </aside>
  )
}

interface StepIconProps {
  isActive: boolean
  isCompleted: boolean
}

function StepIcon({ isActive, isCompleted }: StepIconProps) {
  if (isCompleted) {
    return <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-500" />
  }

  return (
    <Circle
      className={clsx(
        "mt-0.5 h-5 w-5 text-muted-foreground",
        isActive && "text-primary",
      )}
      strokeWidth={isActive ? 3 : 1.5}
    />
  )
}
